// contractSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useInsertData } from "../../hooks/useInsertData";
import { useGetData } from "../../hooks/useGetData";
import useDeleteData from "../../hooks/useDeleteData";
import { useUpdateData } from "../../hooks/useUpdateDate";

// ============================
// Async Thunks
// ============================

export const createContract = createAsyncThunk(
  "contracts/createContract",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useInsertData("contracts", data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getAllContracts = createAsyncThunk(
  "contracts/getAllContracts",
  async (page = 1, { rejectWithValue }) => {
    try {
      const res = await useGetData(`contracts?page=${page}`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// إضافة thunk للبحث - جديد
export const searchContracts = createAsyncThunk(
  "contracts/searchContracts",
  async ({ keyword, page = 1 }, { rejectWithValue }) => {
    try {
      const res = await useGetData(
        `contracts?keyword=${encodeURIComponent(keyword)}&page=${page}`,
      );
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getContractById = createAsyncThunk(
  "contracts/getContractById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useGetData(`contracts/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateContract = createAsyncThunk(
  "contracts/updateContract",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await useUpdateData(`contracts/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteContract = createAsyncThunk(
  "contracts/deleteContract",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useDeleteData(`contracts/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getContractByMonth = createAsyncThunk(
  "contracts/getContractByMonth",
  async (_, { rejectWithValue }) => {
    try {
      const res = await useGetData(`contracts/byMonth`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const createContractTerms = createAsyncThunk(
  "contracts/createContractTerms",
  async (data, { rejectWithValue }) => {
    try {
      const res = await useInsertData("contractTerms", data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getAllContractTerms = createAsyncThunk(
  "contracts/getAllContractTerms",
  async (_, { rejectWithValue }) => {
    try {
      const res = await useGetData("contractTerms");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteContractTerm = createAsyncThunk(
  "contracts/deleteContractTerm",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useDeleteData(`contractTerms/force/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getContractTermById = createAsyncThunk(
  "contracts/getContractTermById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await useGetData(`contractTerms/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateContractTerm = createAsyncThunk(
  "contracts/updateContractTerm",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await useUpdateData(`contractTerms/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// ============================
// Initial State
// ============================

const initialState = {
  contract: null,
  updatedContract: null,
  contractTerms: null,
  allContracts: [], // غير من null إلى []
  searchResults: [], // غير من null إلى []
  allContractTerms: [], // غير من null إلى []
  detailsContract: null,
  detailsContractTerm: null,
  contractByMonth: null,
  status: "idle",
  searchStatus: "idle",
  error: null,
};

// ============================
// Slice
// ============================

export const contractSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Create Contract
      .addCase(createContract.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createContract.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.contract = action.payload;
      })
      .addCase(createContract.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Get All Contracts
      .addCase(getAllContracts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllContracts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allContracts = action.payload;
      })
      .addCase(getAllContracts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Search Contracts - جديد
      .addCase(searchContracts.pending, (state) => {
        state.searchStatus = "loading";
        state.error = null;
      })
      .addCase(searchContracts.fulfilled, (state, action) => {
        state.searchStatus = "succeeded";
        state.searchResults = action.payload;
      })
      .addCase(searchContracts.rejected, (state, action) => {
        state.searchStatus = "failed";
        state.error = action.payload;
      })

      // Get Contract By ID
      .addCase(getContractById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getContractById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.detailsContract = action.payload;
      })
      .addCase(getContractById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Contract
      .addCase(updateContract.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateContract.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.updatedContract = action.payload;
      })
      .addCase(updateContract.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Delete Contract
      .addCase(deleteContract.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteContract.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(deleteContract.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Get Contracts By Month
      .addCase(getContractByMonth.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getContractByMonth.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.contractByMonth = action.payload;
      })
      .addCase(getContractByMonth.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Contract Terms CRUD
      .addCase(getAllContractTerms.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllContractTerms.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allContractTerms = action.payload;
      })
      .addCase(getAllContractTerms.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(createContractTerms.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createContractTerms.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.contractTerms = action.payload;
      })
      .addCase(createContractTerms.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(getContractTermById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getContractTermById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.detailsContractTerm = action.payload;
      })
      .addCase(getContractTermById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(updateContractTerm.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateContractTerm.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.contractTerms = action.payload;
      })
      .addCase(updateContractTerm.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(deleteContractTerm.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteContractTerm.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(deleteContractTerm.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

// ============================
// Exports
// ============================

export const { resetState } = contractSlice.actions;
export default contractSlice.reducer;
